import { Permissions } from "@/type/api";

export const FULL_ACCESS_ROLES = [
  Permissions.Administrator,
  Permissions.SystemManager,
  Permissions.PetAppAdmin,
] as const;

export const HEALTHCARE_ROLES = [
  Permissions.Visit,
  Permissions.Coordinator,
  Permissions.HealthcareCoordinator,
  Permissions.HealthcareAdministrator,
  Permissions.Physician,
  Permissions.LaboratoryUser,
  Permissions.NursingUser,
  ...FULL_ACCESS_ROLES,
];

// Roles allowed to open the chrome-free clinic intake kiosk (/clinic-station).
// A user with ONLY Clinic Reception is locked to the kiosk (see navigation guard).
export const CLINIC_STATION_ROLES = [
  Permissions.ClinicReception,
  ...HEALTHCARE_ROLES,
];

export const GRAVEYARD_ROLES = [
  Permissions.HealthcareAdministrator,
  Permissions.Physician,
  Permissions.HealthcareCoordinator,
  Permissions.Coordinator,
  ...FULL_ACCESS_ROLES,
];

export const ACCOUNTING_ROLES = [
  Permissions.AccountsUser,
  Permissions.AccountsManager,
  ...FULL_ACCESS_ROLES,
];

export const SALES_ACCOUNTING_ROLES = [
  Permissions.AccountsUser,
  Permissions.AccountsManager,
  Permissions.SalesUser,
  Permissions.SalesManager,
  ...FULL_ACCESS_ROLES,
];

export const WAREHOUSE_ROLES = [
  Permissions.StockUser,
  Permissions.StockManager,
  Permissions.ItemManager,
  Permissions.PurchaseUser,
  Permissions.PurchaseManager,
  ...FULL_ACCESS_ROLES,
];

/**
 * Drivers sit between sales (the order), stock (dispatch and returns are Stock Entries)
 * and accounting (collection and handover are Payment Entries), so every one of those
 * desks can open the module. The APIs still enforce POS-profile and warehouse scope.
 */
export const DRIVERS_ROLES = [
  Permissions.SalesUser,
  Permissions.SalesManager,
  Permissions.StockUser,
  Permissions.StockManager,
  Permissions.AccountsUser,
  Permissions.AccountsManager,
  ...FULL_ACCESS_ROLES,
];

/**
 * Delivery partners are a sales relationship settled through accounting: sales desks
 * need to see what an app owes and read a partner's rate, accounting records the
 * monthly remittance. Stock roles are deliberately absent — unlike drivers, nothing
 * here moves goods; the partner order leaves stock through an ordinary POS sale.
 */
export const DELIVERY_PARTNERS_ROLES = [
  Permissions.SalesUser,
  Permissions.SalesManager,
  Permissions.AccountsUser,
  Permissions.AccountsManager,
  ...FULL_ACCESS_ROLES,
];

/**
 * Kiosk games (Play & Win). Managers design games and prizes; reception and the tills
 * only redeem claim codes, so they get the redeem page and nothing else.
 */
export const GAMES_ADMIN_ROLES = [
  Permissions.SalesManager,
  Permissions.AccountsManager,
  Permissions.HealthcareAdministrator,
  ...FULL_ACCESS_ROLES,
];

export const GAMES_REDEEM_ROLES = [
  Permissions.ClinicReception,
  Permissions.SalesUser,
  Permissions.AccountsUser,
  ...GAMES_ADMIN_ROLES,
];

export const USERS_ADMIN_ROLES = [
  Permissions.Users,
  ...FULL_ACCESS_ROLES,
];

export const WHATSAPP_ROLES = [
  Permissions.HealthcareAdministrator,
  ...FULL_ACCESS_ROLES,
];

export const REPORT_ENGINE_ROLES = [
  Permissions.AccountsManager,
  Permissions.SalesManager,
  Permissions.HealthcareAdministrator,
  Permissions.HealthcareCoordinator,
  ...FULL_ACCESS_ROLES,
];

export const MODULE_KEYS = {
  DESK: "module.desk",
  MODULES: "module.modules",
  HRMS: "module.hrms",
  HIS: "module.his",
  CORRESPONDENCE: "module.correspondence",
  HEALTHCARE: "module.healthcare",
  GRAVEYARD: "module.graveyard",
  PET: "module.pet",
  ECOMMERCE: "module.ecommerce",
  ORDER: "module.order",
  POS: "module.pos",
  CRM: "module.crm",
  ACCOUNTING: "module.accounting",
  WAREHOUSE: "module.warehouse",
  DRIVERS: "module.drivers",
  DELIVERY_PARTNERS: "module.delivery_partners",
  PET_TRANSPORT: "module.pet_transport",
  TRANSPORTERS: "module.transporters",
  GAMES: "module.games",
  AUDIT: "module.audit",
  USERS: "module.users",
  GUARDIANS: "module.guardians",
  CUSTOMERS: "module.customers",
  SETTINGS: "module.settings",
  QUEUE: "module.queue",
  REPORT_ENGINE: "module.report_engine",
  WHATSAPP: "module.whatsapp",
} as const;

export const PAGE_KEYS = {
  ROOT: "page.root",
  MODULES: "page.modules",
  HRMS: "page.hrms",
  HIS: "page.his",
  CORRESPONDENCE: "page.correspondence",
  HEALTHCARE_CASE_SHEETS: "page.healthcare.case_sheets",
  HEALTHCARE_CASE_FOLLOW_UP: "page.healthcare.case_follow_up",
  HEALTHCARE_COORDINATOR: "page.healthcare.coordinator",
  HEALTHCARE_COORDINATOR_PILOT: "page.healthcare.coordinator_pilot",
  HEALTHCARE_VISITS: "page.healthcare.visits",
  HEALTHCARE_APPOINTMENTS: "page.healthcare.appointments",
  HEALTHCARE_PHARMACY: "page.healthcare.pharmacy",
  HEALTHCARE_LABS: "page.healthcare.labs",
  HEALTHCARE_RADIOLOGY: "page.healthcare.radiology",
  HEALTHCARE_PREVENTIVE_CARE: "page.healthcare.preventive-care",
  HEALTHCARE_PROCEDURES: "page.healthcare.procedures",
  HEALTHCARE_SERVICES: "page.healthcare.services",
  HEALTHCARE_SERVICE_PROVIDER: "page.healthcare.service_provider",
  HEALTHCARE_BOARDING: "page.healthcare.boarding",
  HEALTHCARE_CLINICAL_MASTER_DATA: "page.healthcare.clinical_master_data",
  HEALTHCARE_UNIVERSAL_PRINT: "page.healthcare.universal_print",
  HEALTHCARE_SETTINGS: "page.healthcare.settings",
  HEALTHCARE_RATINGS: "page.healthcare.ratings",
  HEALTHCARE_DOCTOR_PERFORMANCE: "page.healthcare.doctor_performance",
  QR_SEARCH: "page.qr_search",
  CLINIC_STATION: "page.clinic.station",
  GRAVEYARD_GRAVES: "page.graveyard.graves",
  PET_PROFILE: "page.pet.profile",
  ECOMMERCE: "page.ecommerce",
  ORDER_LIST: "page.order.list",
  POS_SELL: "page.pos.sell",
  /** No screen: the permission to set or change a transport fare's price at the till. */
  POS_TRANSPORT_FARE_PRICE: "page.pos.transport_fare_price",
  CRM_DASHBOARD: "page.crm.dashboard",
  CRM_FOLLOW_UPS: "page.crm.follow_ups",
  CRM_LEADS: "page.crm.leads",
  CRM_CUSTOMERS: "page.crm.customers",
  CRM_TERRITORIES: "page.crm.territories",
  CRM_PIPELINE: "page.crm.pipeline",
  CRM_OPPORTUNITIES: "page.crm.opportunities",
  CRM_MESSAGES: "page.crm.messages",
  CRM_CAMPAIGNS: "page.crm.campaigns",
  CRM_REPORTS: "page.crm.reports",
  CRM_SETTINGS: "page.crm.settings",
  ACCOUNTING_SALES_INVOICES: "page.accounting.sales_invoices",
  ACCOUNTING_SUPPLIERS: "page.accounting.suppliers",
  ACCOUNTING_PAYMENT_ENTRIES: "page.accounting.payment_entries",
  ACCOUNTING_JOURNAL_ENTRIES: "page.accounting.journal_entries",
  ACCOUNTING_CASHIERS: "page.accounting.cashiers",
  ACCOUNTING_SMART_DASHBOARD: "page.accounting.smart_dashboard",
  ACCOUNTING_GENERAL_LEDGER: "page.accounting.general_ledger",
  ACCOUNTING_ACCOUNT_TREE: "page.accounting.account_tree",
  ACCOUNTING_SETTINGS: "page.accounting.settings",
  WAREHOUSE_LIST: "page.warehouse.list",
  WAREHOUSE_ITEMS: "page.warehouse.items",
  WAREHOUSE_STOCK_TRANSFERS: "page.warehouse.stock_transfers",
  WAREHOUSE_STOCK_ENTRIES: "page.warehouse.stock_entries",
  WAREHOUSE_STOCK_COUNT: "page.warehouse.stock_count",
  WAREHOUSE_STOCK_RECONCILIATIONS: "page.warehouse.stock_reconciliations",
  WAREHOUSE_STOCK_CHECKER: "page.warehouse.stock_checker",
  WAREHOUSE_STOCK_LEDGER: "page.warehouse.stock_ledger",
  WAREHOUSE_RETURNS_MONITOR: "page.warehouse.returns_monitor",
  WAREHOUSE_SMART_DASHBOARD: "page.warehouse.smart_dashboard",
  WAREHOUSE_PURCHASE_INVOICES: "page.warehouse.purchase_invoices",
  WAREHOUSE_ITEM_PRICES: "page.warehouse.item_prices",
  WAREHOUSE_STOCK_PRICES: "page.warehouse.stock_prices",
  ITEM_UNITS_LIST: "page.item_units.list",
  /**
   * ⚠️ ROLLOUT STEP, NOT OPTIONAL. `canPage` refuses any key absent from the backend
   * access snapshot, and the registry's `fallbackRoles` grant nothing on a backend-driven
   * deployment (docs/AGENT.md, "Page Access finally honors backend settings"). Before
   * announcing these pages: open `/permission`, run the frontend-page sync, and grant the
   * six keys to the delivery roles. Until then they are blocked for everyone except a
   * full-access bootstrap user. Do NOT add `MODULE_KEYS.DRIVERS` to
   * `FRONTEND_REGISTERED_ROLE_FALLBACK_MODULES` as a shortcut — that set only fires when
   * there is no backend snapshot, and there it over-grants by role.
   */
  DRIVERS_LIST: "page.drivers.list",
  DRIVERS_DETAIL: "page.drivers.detail",
  DRIVERS_ORDERS: "page.drivers.orders",
  DRIVERS_DISPATCH: "page.drivers.dispatch",
  DRIVERS_SETTLEMENT: "page.drivers.settlement",
  DRIVERS_DASHBOARD: "page.drivers.dashboard",
  /**
   * The DRIVER's own screen (`/drivers/me`). Granted to the role the drivers' own logins
   * hold — not to the delivery desk. Same sync-and-grant rollout step as the keys above.
   */
  DRIVERS_ME: "page.drivers.me",
  /**
   * ⚠️ SAME ROLLOUT STEP AS THE DRIVER KEYS ABOVE. Registering these gates the pages:
   * until `/permission` has run the frontend-page sync and the keys have been granted,
   * only a full-access bootstrap user can open Delivery Partners.
   */
  DELIVERY_PARTNERS_LIST: "page.delivery_partners.list",
  PET_TRANSPORT_ME: "page.pet_transport.me",
  /** The clinic's own transporters (their own record, not delivery partners). Sync and grant in /permission. */
  TRANSPORTERS_LIST: "page.transporters.list",
  TRANSPORTERS_DETAIL: "page.transporters.detail",
  DELIVERY_PARTNERS_DETAIL: "page.delivery_partners.detail",
  /**
   * ⚠️ SAME ROLLOUT STEP AS THE DRIVER KEYS ABOVE. Registering these gates the Games
   * pages: nobody but a full-access bootstrap user sees them until `/permission` has
   * synced the frontend page keys and granted them.
   */
  GAMES_DASHBOARD: "page.games.dashboard",
  GAMES_LIST: "page.games.list",
  GAMES_DETAIL: "page.games.detail",
  GAMES_PRIZES: "page.games.prizes",
  GAMES_PLAYS: "page.games.plays",
  GAMES_REDEEM: "page.games.redeem",
  AUDIT_SYSTEM: "page.audit.system",
  AUDIT_RECORD_HISTORY: "page.audit.record_history",
  AUDIT_SETTLEMENT_DIFFERENCES: "page.audit.settlement_differences",
  USERS_ROLES: "page.users.roles",
  USERS_PROFILE: "page.users.profile",
  USERS_PERMISSIONS: "page.users.permissions",
  USERS_ACCESS_DESIGNER: "page.users.access_designer",
  GUARDIANS_LIST: "page.guardians.list",
  CUSTOMERS_LIST: "page.customers.list",
  CUSTOMER_GROUPS: "page.customers.groups",
  CUSTOMERS_TERRITORY_INSIGHTS: "page.customers.territory_insights",
  SETTINGS: "page.settings",
  QUEUE_DOCTOR: "page.queue.doctor",
  REPORT_ENGINE: "page.report_engine",
  WHATSAPP: "page.whatsapp",
} as const;

export const ACTION_KEYS = {
  USERS_PERMISSIONS_CREATE: "action.users.permissions.create",
  USERS_PERMISSIONS_UPDATE: "action.users.permissions.update",
  USERS_PERMISSIONS_DELETE: "action.users.permissions.delete",
  USERS_PERMISSIONS_EXPORT: "action.users.permissions.export",
  USERS_PERMISSIONS_PRINT: "action.users.permissions.print",
  HEALTHCARE_APPOINTMENTS_CREATE: "action.healthcare.appointments.create",
  HEALTHCARE_APPOINTMENTS_EXPORT: "action.healthcare.appointments.export",
  HEALTHCARE_APPOINTMENTS_PRINT: "action.healthcare.appointments.print",
  HEALTHCARE_APPOINTMENTS_CONVERT: "action.healthcare.appointments.convert_to_visit",
  GRAVEYARD_REPORT: "action.graveyard.report",
  GRAVEYARD_CONFIRM: "action.graveyard.confirm",
  GRAVEYARD_MANAGER_REVIEW: "action.graveyard.manager_review",
  GRAVEYARD_FINALIZE: "action.graveyard.finalize",
  GRAVEYARD_ISSUE_CERTIFICATE: "action.graveyard.issue_certificate",
  GRAVEYARD_CANCEL: "action.graveyard.cancel",
  ACCOUNTING_SALES_INVOICES_CREATE: "action.accounting.sales_invoices.create",
  ACCOUNTING_SALES_INVOICES_UPDATE: "action.accounting.sales_invoices.update",
  ACCOUNTING_SALES_INVOICES_SUBMIT: "action.accounting.sales_invoices.submit",
  ACCOUNTING_SALES_INVOICES_PRINT: "action.accounting.sales_invoices.print",
  ACCOUNTING_SALES_INVOICES_ADD_PAYMENT: "action.accounting.sales_invoices.add_payment",
  ACCOUNTING_PAYMENT_ENTRIES_CREATE: "action.accounting.payment_entries.create",
  ACCOUNTING_PAYMENT_ENTRIES_SUBMIT: "action.accounting.payment_entries.submit",
  ACCOUNTING_JOURNAL_ENTRIES_CREATE: "action.accounting.journal_entries.create",
  ACCOUNTING_JOURNAL_ENTRIES_SUBMIT: "action.accounting.journal_entries.submit",
  ACCOUNTING_CASHIERS_CREATE: "action.accounting.cashiers.create",
  POS_SELL_CREATE_SALE: "action.pos.sell.create_sale",
  POS_SELL_CREATE_CUSTOMER: "action.pos.sell.create_customer",
  POS_SELL_PRINT: "action.pos.sell.print",
  WAREHOUSE_CREATE: "action.warehouse.create",
  WAREHOUSE_ITEMS_CREATE: "action.warehouse.items.create",
  WAREHOUSE_ITEMS_UPDATE: "action.warehouse.items.update",
  WAREHOUSE_ITEMS_DELETE: "action.warehouse.items.delete",
  WAREHOUSE_ITEM_PRICES_CREATE: "action.warehouse.item_prices.create",
  WAREHOUSE_ITEM_PRICES_UPDATE: "action.warehouse.item_prices.update",
  WAREHOUSE_ITEM_PRICES_DELETE: "action.warehouse.item_prices.delete",
  WAREHOUSE_STOCK_PRICES_CREATE: "action.warehouse.stock_prices.create",
  WAREHOUSE_STOCK_PRICES_SUBMIT: "action.warehouse.stock_prices.submit",
  WAREHOUSE_STOCK_PRICES_CANCEL: "action.warehouse.stock_prices.cancel",
  WAREHOUSE_PURCHASE_INVOICES_CREATE: "action.warehouse.purchase_invoices.create",
  WAREHOUSE_PURCHASE_INVOICES_SUBMIT: "action.warehouse.purchase_invoices.submit",
  WAREHOUSE_PURCHASE_INVOICES_ADD_PAYMENT: "action.warehouse.purchase_invoices.add_payment",
  WAREHOUSE_STOCK_ENTRIES_CREATE: "action.warehouse.stock_entries.create",
  WAREHOUSE_STOCK_ENTRIES_SUBMIT: "action.warehouse.stock_entries.submit",
  WAREHOUSE_STOCK_RECONCILIATIONS_CREATE: "action.warehouse.stock_reconciliations.create",
  WAREHOUSE_STOCK_RECONCILIATIONS_SUBMIT: "action.warehouse.stock_reconciliations.submit",
  ITEM_UNITS_CREATE: "action.item_units.create",
  ITEM_UNITS_UPDATE: "action.item_units.update",
  ITEM_UNITS_DELETE: "action.item_units.delete",
  REPORT_ENGINE_EXPORT: "action.report_engine.export",
  REPORT_ENGINE_PRINT: "action.report_engine.print",
  REPORT_ENGINE_IMPORT: "action.report_engine.import",
} as const;
